import React, { useState, useEffect, useRef } from 'react';
import { ChatListScreen, ChatRoomScreen, CreateChatScreen } from '../chat-module';
import ChatService from '../chat-module/services/ChatService';
import { currentDateFormat } from "../utils";
import theme from '../chat-module/styles/theme';
import './Chat.css';

/**
 * Chat - Main chat page component
 * Integrates ChatListScreen and ChatRoomScreen with live backend
 */
const Chat = () => {
  const [currentView, setCurrentView] = useState('list'); // 'list', 'room', or 'create'
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [userData, setUserData] = useState(null);
  const [currentDate] = useState(new Date());
  const [onlineUsers, setOnlineUsers] = useState(new Set()); // Shared online users state
  const onlineStatusListenerRef = useRef(null);


  useEffect(() => {
    // Get user data from localStorage
    const storedUserData = localStorage.getItem('userData');
    
    if (storedUserData) {
      try {
        const parsed = JSON.parse(storedUserData);
        setUserData(parsed);
      } catch (e) {
        console.error('❌ Error parsing userData:', e);
        console.error('❌ Raw userData:', storedUserData);
      }
    } else {
      console.warn('⚠️ No userData found in localStorage. User may need to log in.');
    }
  }, []);

  // Shared online users state management - syncs both ChatListScreen and ChatRoomScreen
  useEffect(() => {
    if (!userData) return;

    // Listen for online/offline status updates
    const onlineStatusCallback = (data) => {
      const userId = data.userId || data.user_id;
      const online = data.online === true || data.online === 1 || data.online === 'true';
      
      if (!userId) return;
      
      setOnlineUsers(prev => {
        const newSet = new Set(prev);
        const userIdStr = String(userId);
        
        // Remove any existing entry (handles type mismatches - string vs number)
        newSet.forEach(id => {
          if (String(id) === userIdStr) {
            newSet.delete(id);
          }
        });
        
        if (online) {
          // Add the userId (keep original type for consistency)
          newSet.add(userId);
        }
        // If offline, we've already removed it above
        
        return newSet;
      });
    };

    ChatService.onOnlineStatus(onlineStatusCallback);
    onlineStatusListenerRef.current = onlineStatusCallback;

    // Request online users on mount
    ChatService.requestOnlineUsers();
    setTimeout(() => ChatService.requestOnlineUsers(), 500);
    setTimeout(() => ChatService.requestOnlineUsers(), 2000);

    // Cleanup
    return () => {
      if (onlineStatusListenerRef.current) {
        ChatService.removeOnlineStatusListener(onlineStatusListenerRef.current);
        onlineStatusListenerRef.current = null;
      }
    };
  }, [userData]);

  // Map user role from userData to chat module format
  // CRITICAL: Based on actual database role table:
  //   role_id: 1 = Administrator → 'admin'
  //   role_id: 2 = Agency → 'admin' (Agency users are admins)
  //   role_id: 3 = Office → (not used in chat)
  //   role_id: 4 = Driver → 'driver'
  //   role_id: 5 = Passenger → (not used in chat)
  //   role_id: 6 = Office Admin → 'officeAdmin'
  const getUserRole = () => {
    if (!userData) return null;
    
    const userRoleValue = userData.user_role;
    const userId = userData.user_id || userData.id || userData.login_user_name;
    const userName = userData.full_name || userData.name || userData.user_name;
    const agencyId = userData.agency_id;
    let mappedRole;
    
    // Map based on ACTUAL database role table
    switch (userRoleValue) {
      case 1: // Administrator
        mappedRole = 'admin';
        break;
      case 2: // Agency (these are admins, not drivers!)
        mappedRole = 'admin';
        break;
      case 4: // Driver
        mappedRole = 'driver';
        break;
      case 6: // Office Admin
        mappedRole = 'officeAdmin';
        break;
      case 3: // Office (not typically used in chat)
      case 5: // Passenger (not used in chat)
      default:
        // Default fallback to admin for safety
        mappedRole = 'admin';
        console.warn(`⚠️ Unknown user_role value: ${userRoleValue}, defaulting to 'admin'`);
        break;
    }
    
    return mappedRole;
  };

  // Get user ID for chat (admin format: admin_{agencyId})
  // IMPORTANT: For admin users, we need to check if they should use admin_CT123 format
  // or their actual userId. The backend may return different rooms based on this.
  // For officeAdmin and driver, use their actual userId (e.g., "302" for officeAdmin)
  const getUserId = () => {
    if (!userData) return null;
    
    const userRole = getUserRole();
    if (userRole === 'admin') {
      // For admin users, always use admin_{agencyId} format to match Flutter behavior
      // This ensures we request rooms where admin_CT123 is a participant
      // Note: If backend returns rooms with userId "1" instead, fallback logic will handle it
      return `admin_${userData.agency_id}`;
    }
    // For officeAdmin and driver, return the user_id as string (e.g., "302" for officeAdmin)
    // This matches Flutter behavior where officeAdmin uses actual userId
    // Check both user_id and id fields
    const userId = userData.user_id || userData.id || userData.login_user_name || null;
    const userIdStr = userId ? String(userId) : null;
    
    return userIdStr;
  };

  const handleChatRoomClick = (room) => {
    setSelectedRoom(room);
    setCurrentView('room');
    // Notify ChatListScreen that a room is now open (for sound notifications)
    // This will be passed via a ref or context in future if needed
  };

  const handleBackToList = () => {
    setCurrentView('list');
    setSelectedRoom(null);
  };

  const handleOpenCreateChat = () => {
    setCurrentView('create');
  };

  const handleChatRoomCreated = (room) => {
    setSelectedRoom(room);
    setCurrentView('room');
    // Refresh chat list if needed
    if (window.__refreshChatList) {
      window.__refreshChatList();
    }
  };

  // Don't render until we have user data
  if (!userData) {
    return (
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column',
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        gap: '20px'
      }}>
        <div style={{ fontSize: '18px' }}>Loading user data...</div>
        <div style={{ fontSize: '14px', color: '#666' }}>
          {localStorage.getItem('userData') 
            ? 'Parsing user data...' 
            : 'No user data found. Please log in first.'}
        </div>
      </div>
    );
  }

  const userId = getUserId();
  const userName = userData.full_name || userData.name || 'User';
  const userRole = getUserRole();
  const agencyId = userData.agency_id?.toString() || '';

  // CRITICAL: Verify userRole is correct before passing to API
  if (userRole !== 'admin' && userRole !== 'driver' && userRole !== 'officeAdmin') {
    console.error(`❌ INVALID USER ROLE: "${userRole}". Must be 'admin', 'driver', or 'officeAdmin'`);
    console.error(`❌ This will cause API to return incorrect results!`);
  }
  
  // Role validation checks
  const actualUserId = userData.user_id || userData.id || userData.login_user_name;
  
  // Check: role_id 2 (Agency) should map to 'admin'
  if (userData.user_role === 2 && userRole !== 'admin') {
    console.error(`❌ CRITICAL: User has user_role=2 (Agency) but getUserRole() returned "${userRole}"`);
    console.error(`❌ Agency users (role_id=2) should be mapped to 'admin'`);
  }
  
  // Check: role_id 1 (Administrator) should map to 'admin'
  if (userData.user_role === 1 && userRole !== 'admin') {
    console.error(`❌ CRITICAL: User has user_role=1 (Administrator) but getUserRole() returned "${userRole}"`);
    console.error(`❌ Administrator users (role_id=1) must have userRole='admin'`);
  }
  
  // Check: role_id 4 (Driver) should map to 'driver'
  if (userData.user_role === 4 && userRole !== 'driver') {
    console.error(`❌ CRITICAL: User has user_role=4 (Driver) but getUserRole() returned "${userRole}"`);
    console.error(`❌ Driver users (role_id=4) must have userRole='driver'`);
  }
  
  // Check: role_id 6 (Office Admin) should map to 'officeAdmin'
  if (userData.user_role === 6 && userRole !== 'officeAdmin') {
    console.error(`❌ CRITICAL: User has user_role=6 (Office Admin) but getUserRole() returned "${userRole}"`);
    console.error(`❌ Office Admin users (role_id=6) must have userRole='officeAdmin'`);
  }

  if (!userId || !agencyId) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh' 
      }}>
        <div>Error: Missing user information. Please log in again.</div>
      </div>
    );
  }

  return (
    <>
      {/* Header Section */}
      <section className='pl-5 pr-5' style={{ marginTop: '0px' }}>
        <div className='d-flex justify-content-between form-container' style={{ borderBottom: 'solid 1px #eee' }}>
          <div>
            <h2 className="fs-5 font-weight-600">Chat</h2>
          </div>
          <div>
            <span style={{ fontSize: '15px' }}>{currentDateFormat(currentDate)}</span>
          </div>
        </div>
      </section>

      {/* Chat Content - Side-by-side layout for web, stacked for mobile */}
      <div className={`chat-container ${selectedRoom ? 'has-selected-room' : ''}`} style={{ position: 'relative' }}>
        {/* Left Side: Chat List (always visible on web) */}
        <div 
          className="chat-list-panel"
          style={{
            display: currentView === 'create' ? 'none' : 'flex'
          }}
        >
          <ChatListScreen
            currentUserId={userId}
            currentUserName={userName}
            currentUserRole={userRole}
            currentAgencyId={agencyId}
            onChatRoomClick={handleChatRoomClick}
            onCreateChat={handleOpenCreateChat}
            onlineUsers={onlineUsers}
            setOnlineUsers={setOnlineUsers}
          />
        </div>

        {/* Right Side: Chat Room or Create Chat or Empty State */}
        <div className="chat-room-panel">
          {currentView === 'create' ? (
            <CreateChatScreen
              currentUserId={userId}
              currentUserName={userName}
              currentUserRole={userRole}
              currentAgencyId={agencyId}
              onBack={handleBackToList}
              onChatRoomCreated={handleChatRoomCreated}
            />
          ) : selectedRoom ? (
            <ChatRoomScreen
              room={selectedRoom}
              currentUserId={userId}
              currentUserName={userName}
              currentUserRole={userRole}
              onBack={null}
              showBackButton={false}
              onlineUsers={onlineUsers}
              setOnlineUsers={setOnlineUsers}
            />
          ) : (
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column',
              justifyContent: 'center', 
              alignItems: 'center', 
              height: '100%',
              background: 'linear-gradient(135deg, #fafbfc 0%, #f8f9fa 100%)',
              padding: '2rem 1.5rem',
              position: 'relative',
              overflow: 'hidden'
            }}>
              {/* Decorative background elements - Enhanced colors */}
              <div style={{
                position: 'absolute',
                top: '-50px',
                right: '-50px',
                width: '200px',
                height: '200px',
                borderRadius: '50%',
                background: `radial-gradient(circle, rgba(50, 130, 127, 0.06) 0%, transparent 65%)`,
                pointerEvents: 'none'
              }}></div>
              <div style={{
                position: 'absolute',
                bottom: '-40px',
                left: '-40px',
                width: '180px',
                height: '180px',
                borderRadius: '50%',
                background: `radial-gradient(circle, rgba(13, 148, 136, 0.05) 0%, transparent 65%)`,
                pointerEvents: 'none'
              }}></div>

              {/* Circular Icon with Speech Bubble - Enhanced Color */}
              <div style={{
                width: '68px',
                height: '68px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '1.25rem',
                background: '#0d9488',
                boxShadow: '0 8px 20px rgba(13, 148, 136, 0.3), 0 4px 10px rgba(13, 148, 136, 0.2)',
                position: 'relative',
                zIndex: 1
              }}>
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  width="32" 
                  height="32" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="#ffffff" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                  style={{
                    filter: 'drop-shadow(0 2px 3px rgba(0, 0, 0, 0.2))'
                  }}
                >
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                </svg>
              </div>

              {/* Main Heading */}
              <h2 style={{ 
                fontSize: '20px', 
                fontWeight: 600, 
                marginBottom: '0.625rem',
                color: '#374151',
                margin: '0 0 0.625rem 0',
                letterSpacing: '-0.01em',
                position: 'relative',
                zIndex: 1
              }}>
                Select a conversation
              </h2>

              {/* Instructional Text */}
              <p style={{ 
                fontSize: '13px', 
                color: '#9ca3af',
                margin: '0 0 1.5rem 0',
                textAlign: 'center',
                maxWidth: '380px',
                lineHeight: '1.5',
                position: 'relative',
                zIndex: 1
              }}>
                Choose a chat from the list on the left to start messaging or creating new trips.
              </p>

              {/* New Message Button - Enhanced Color */}
              <button
                onClick={handleOpenCreateChat}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                  padding: '0.6875rem 1.5rem',
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#ffffff',
                  background: '#0d9488',
                  border: 'none',
                  borderRadius: theme.borderRadius.md,
                  cursor: 'pointer',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  boxShadow: '0 4px 12px rgba(13, 148, 136, 0.35), 0 2px 6px rgba(13, 148, 136, 0.25)',
                  position: 'relative',
                  zIndex: 1
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#0f766e';
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow = '0 6px 18px rgba(13, 148, 136, 0.45), 0 3px 8px rgba(13, 148, 136, 0.35)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#0d9488';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(13, 148, 136, 0.35), 0 2px 6px rgba(13, 148, 136, 0.25)';
                }}
              >
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  width="16" 
                  height="16" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="currentColor" 
                  strokeWidth="2.5" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                >
                  <line x1="12" y1="5" x2="12" y2="19"></line>
                  <line x1="5" y1="12" x2="19" y2="12"></line>
                </svg>
                <span>New Message</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Chat;
